import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { User, Smartphone, X, RefreshCw, ArrowLeft } from "lucide-react";
import { API_URL, axios } from "../../constants";

/**
 * Centralised, in-page login modal used by the DoozyGo feature gate.
 * Reuses the existing customer-auth endpoints (phone OTP + Emergent Google OAuth).
 * Phone OTP logs in without leaving the page; Google does a full redirect.
 */
export default function AuthGateModal({ open, onClose, onLoginSuccess, startGoogle }) {
  const [mode, setMode] = useState("select"); // select | phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpPhone, setOtpPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setMode("select"); setPhone(""); setOtp(""); setOtpPhone("");
      setError(""); setCooldown(0);
    }
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!open) return null;

  const sendOtp = async (force = false) => {
    if (sendingRef.current) return;
    const cleaned = phone.replace(/\D/g, "").slice(-10);
    if (cleaned.length !== 10) { setError("Enter a valid 10-digit mobile number"); return; }
    sendingRef.current = true; setSending(true); setError("");
    const reqId = `otp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      await axios.post(`${API_URL}/api/customer-auth/phone/send-otp`, { phone: cleaned, force, request_id: reqId });
      setOtpPhone(cleaned); setMode("otp"); setCooldown(90);
    } catch (e) { setError(e.response?.data?.detail || "Failed to send OTP."); }
    finally { setSending(false); sendingRef.current = false; }
  };

  const verifyOtp = async () => {
    if (otp.length < 4) { setError("Enter the OTP code"); return; }
    setVerifying(true); setError("");
    try {
      const res = await axios.post(`${API_URL}/api/customer-auth/phone/verify-otp`, { phone: otpPhone, code: otp }, { withCredentials: true });
      await onLoginSuccess(res.data.session_token);
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid OTP.";
      setError(msg); setVerifying(false);
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("new code")) setCooldown(0);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="auth-gate-modal">
      <div className="absolute inset-0 bg-[#1E1B4B]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-[#f0e6d9] p-7 text-center animate-in fade-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full text-[#94a3b8] hover:bg-gray-100 hover:text-[#1E1B4B] transition-colors" data-testid="auth-gate-close" aria-label="Close">
          <X size={18} />
        </button>

        <div className="w-14 h-14 bg-[#E83E8C]/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <User size={28} className="text-[#E83E8C]" />
        </div>
        <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">Sign in to continue</h2>
        <p className="text-sm text-[#475569] mb-5">Log in or create your free DoozyGo® account to use this feature.</p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between" data-testid="auth-gate-error">
            <span>{error}</span>
            <button onClick={() => setError("")} className="text-red-400 hover:text-red-600"><X size={14} /></button>
          </div>
        )}

        {mode === "select" && (
          <div className="space-y-3">
            <button onClick={startGoogle} className="flex items-center justify-center gap-3 w-full px-5 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#4285F4] hover:shadow-md transition-all text-[#1E1B4B] font-medium" data-testid="auth-gate-google-btn">
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>
            <button onClick={() => { setMode("phone"); setError(""); }} className="flex items-center justify-center gap-3 w-full px-5 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#E83E8C] hover:shadow-md transition-all text-[#1E1B4B] font-medium" data-testid="auth-gate-phone-btn">
              <Smartphone size={20} className="text-[#E83E8C]" /> Continue with Mobile Number
            </button>
            <p className="text-[10px] text-[#94a3b8] mt-3">
              By signing in, you agree to our <Link to="/terms-of-service" className="underline" onClick={onClose}>Terms</Link> &amp; <Link to="/privacy-policy" className="underline" onClick={onClose}>Privacy Policy</Link>
            </p>
          </div>
        )}

        {mode === "phone" && (
          <div className="space-y-4">
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-[#E83E8C] transition-colors">
              <span className="px-3 py-3 bg-gray-50 text-sm text-[#475569] font-medium border-r">+91</span>
              <input
                type="tel" value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Enter mobile number" autoFocus
                className="flex-1 px-3 py-3 text-sm outline-none" data-testid="auth-gate-phone-input"
              />
            </div>
            <button onClick={() => sendOtp(false)} disabled={sending || phone.length < 10}
              className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold disabled:opacity-50 hover:bg-[#d1356f] transition-colors"
              data-testid="auth-gate-send-otp-btn">
              {sending ? "Sending OTP..." : "Send OTP"}
            </button>
            <button onClick={() => { setMode("select"); setError(""); }} className="text-sm text-[#475569] hover:text-[#E83E8C] flex items-center gap-1 mx-auto">
              <ArrowLeft size={14} /> Back to all options
            </button>
          </div>
        )}

        {mode === "otp" && (
          <div className="space-y-4">
            <p className="text-sm text-[#475569]">OTP sent to <span className="font-semibold text-[#1E1B4B]">+91 {otpPhone}</span></p>
            <input
              type="text" inputMode="numeric" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Enter 6-digit OTP" autoFocus
              className="w-full px-4 py-3 text-center text-lg tracking-[0.3em] font-mono border-2 border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none transition-colors"
              data-testid="auth-gate-otp-input"
            />
            <button onClick={verifyOtp} disabled={verifying || otp.length < 4}
              className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold disabled:opacity-50 hover:bg-[#d1356f] transition-colors"
              data-testid="auth-gate-verify-otp-btn">
              {verifying ? "Verifying..." : "Verify & Continue"}
            </button>
            <div className="flex items-center justify-center gap-4 text-sm">
              <button onClick={() => sendOtp(true)} disabled={cooldown > 0 || sending}
                className={`flex items-center gap-1 ${cooldown > 0 ? "text-[#94a3b8]" : "text-[#E83E8C] hover:underline"}`}
                data-testid="auth-gate-resend-otp-btn">
                <RefreshCw size={12} />{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={() => { setMode("phone"); setOtp(""); }} className="text-[#475569] hover:text-[#E83E8C]">Change number</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
